// ImagepasteToastOverlay — imagepaste 拒否を**表示中キャンバスの viewport 中央**に
// 出す短命トースト (2026-06-14)。
//
// ## なぜキャンバス中央か
// 画像貼付の瞬間、ユーザの視線はキャンバス (= プレビューや貼り先) に集中している。
// 拒否理由をチャット欄の system 行に出しても気付かれにくい (β-feedback)。そこで
// 拒否 (rate-limited / サイズ超過 / 非対応形式 / 権限なし / アップロード失敗 / ID 無効)
// と、クライアント事前バリデーション拒否を **キャンバスの見えている範囲の中央**に
// オーバーレイ表示する。
//
// ## 位置計算
// `ImagepasteOverlay` の流儀を踏襲: `CanvasViewMapper` (`viewport.ts` §1b) で canvas 全体の
// client 外接矩形 (= viewport-px) を求め、**見えている範囲の中央**を viewport-px で出す。canvas が
// scroll コンテナで一部しか見えていない場合に備え、canvas rect を viewport 矩形と
// 交差させた可視領域の中央を採る (= 画面外に出ない)。flip (左右反転表示) は中央点の
// X に影響しないので考慮不要 (= 中央は鏡像でも中央)。computeOverlayAnchor 同様、
// 計算本体は純粋関数 `computeToastCenter` に切り出して node 環境でテストする。
//
// ## 自動消滅
// - rate-limited 以外: 約 3 秒で自動消滅 (`autoDismissMs`)。
// - rate-limited: useImagepaste がローカルで毎秒カウントダウンし、残り 0 で
//   トースト state を null にする (= ここは渡された text を表示するだけ)。

import { useEffect, useState } from 'react';

import type { CanvasViewMapper } from '../../../infrastructure/drawing/viewport.js';

export interface ImagepasteToast {
  /** 表示テキスト (rate-limited はカウントダウン更新ごとに差し替わる)。 */
  readonly text: string;
  /**
   * トーストの id (= 新しい拒否のたびにインクリメント)。同じ id のあいだは
   * auto-dismiss タイマーを貼り直さない (= カウントダウン更新で消滅時刻が伸びない)。
   */
  readonly id: number;
  /**
   * rate-limited か。true のときは useImagepaste 側のカウントダウンが 0 で消すので
   * このコンポーネントの自動消滅タイマーは動かさない。false (静的文言) のときだけ
   * `autoDismissMs` 後に `onDismiss` を呼ぶ。
   */
  readonly countdown: boolean;
}

export interface ImagepasteToastOverlayProps {
  /** 画面 ⇔ canvas 座標の変換窓口 (= ImagepasteOverlay と同じ)。canvas 全体の client 矩形を得る。 */
  readonly viewMapper: CanvasViewMapper;
  /** 現在のトースト。null で非表示。 */
  readonly toast: ImagepasteToast | null;
  /**
   * 静的文言トーストの自動消滅を親へ通知 (= toast state を null にする)。
   * countdown=true のトーストでは呼ばれない。
   */
  readonly onDismiss: () => void;
  /** 静的文言トーストの表示時間 (ms)。既定 3000。 */
  readonly autoDismissMs?: number;
}

interface ToastCenter {
  readonly left: number;
  readonly top: number;
}

/**
 * 表示中 canvas の「見えている範囲」の中央を viewport-px で返す純粋関数。
 * canvas rect を viewport 矩形と交差させ (= scroll で一部しか見えていない時の対策)、
 * その交差矩形の中央を採る。交差が空 (= canvas 完全に画面外) のときは viewport 中央へ
 * フォールバック (= トーストを必ずユーザに見せる)。
 */
export const computeToastCenter = (input: {
  readonly canvasRect: { left: number; top: number; width: number; height: number };
  readonly viewport: { width: number; height: number };
}): ToastCenter => {
  const { canvasRect, viewport } = input;
  // canvas rect ∩ viewport.
  const visLeft = Math.max(0, canvasRect.left);
  const visTop = Math.max(0, canvasRect.top);
  const visRight = Math.min(viewport.width, canvasRect.left + canvasRect.width);
  const visBottom = Math.min(viewport.height, canvasRect.top + canvasRect.height);
  if (visRight <= visLeft || visBottom <= visTop) {
    // canvas が完全に画面外 → viewport 中央へ。
    return { left: viewport.width / 2, top: viewport.height / 2 };
  }
  return { left: (visLeft + visRight) / 2, top: (visTop + visBottom) / 2 };
};

/**
 * canvas rect / window の変化 (resize / scroll) に追従して中央点を再計算するフック。
 * ImagepasteOverlay の useOverlayAnchor と同流儀。
 */
/** mapper から canvas 全体の client 外接矩形を得る (回転しても外接矩形で返る)。 */
const canvasClientRect = (
  viewMapper: CanvasViewMapper,
): { left: number; top: number; width: number; height: number } => {
  const t = viewMapper.getTransform();
  return viewMapper.canvasRectToClientBounds(0, 0, t.canvasWidth, t.canvasHeight);
};

const useToastCenter = (viewMapper: CanvasViewMapper, visible: boolean): ToastCenter | null => {
  const [center, setCenter] = useState<ToastCenter | null>(null);

  useEffect(() => {
    if (!visible) {
      setCenter(null);
      return;
    }
    if (typeof window === 'undefined') return;
    const recompute = (): void => {
      setCenter(
        computeToastCenter({
          canvasRect: canvasClientRect(viewMapper),
          viewport: { width: window.innerWidth, height: window.innerHeight },
        }),
      );
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true, capture: true });

    // 2026-09-29 viewport transform 移行 (決定5、fable-advisor 指摘): 旧 scroll
    // 方式は pan/zoom-anchor 補正が `wrapper.scrollLeft/Top` を書き換えるたびに
    // scroll イベントが window の capture リスナーへバブルし recompute を間接的に
    // 駆動していたが、新方式は `transform` を直接書くだけで DOM イベントが発火
    // しないため、トースト表示中 (最大 ~3秒) に pan/zoom すると追従しなくなる。
    // 軽量な rAF ポーリングで補う (`ImagepasteOverlay.useOverlayAnchor` と同流儀、
    // 変化が無いフレームは setState しない)。
    let raf = 0;
    let last: ToastCenter | null = null;
    const tick = (): void => {
      raf = window.requestAnimationFrame(tick);
      const next = computeToastCenter({
        canvasRect: canvasClientRect(viewMapper),
        viewport: { width: window.innerWidth, height: window.innerHeight },
      });
      if (last !== null && last.left === next.left && last.top === next.top) return;
      last = next;
      setCenter(next);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, { capture: true });
      window.cancelAnimationFrame(raf);
    };
  }, [viewMapper, visible]);

  return center;
};

export const ImagepasteToastOverlay = ({
  viewMapper,
  toast,
  onDismiss,
  autoDismissMs = 3_000,
}: ImagepasteToastOverlayProps): React.ReactNode => {
  const center = useToastCenter(viewMapper, toast !== null);

  // 静的文言トーストの自動消滅。countdown=true (rate-limited) は useImagepaste 側の
  // カウントダウンが 0 で消すので、ここではタイマーを貼らない。toast.id をキーに
  // するので、同じトーストの再 render では消滅時刻が伸びない (多重トースト時は最新の
  // id で貼り直す)。
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps は「タイマー貼り直しの契機」(= 新しい静的トーストの id / countdown 切替) であって、effect 本体が読む toast 全体ではない。toast.text を含めると countdown 更新で消滅時刻が伸びてしまうため、意図的に id + countdown のみを指定する。
  useEffect(() => {
    if (toast === null || toast.countdown) return;
    const handle = setTimeout(onDismiss, autoDismissMs);
    return () => {
      clearTimeout(handle);
    };
  }, [toast?.id, toast?.countdown, autoDismissMs, onDismiss]);

  if (toast === null || center === null) return null;

  return (
    // viewport-px の中央に置き、transform で自身の中心をそこへ合わせる。position:fixed
    // なので scroll しても画面に貼り付く (= recompute が canvas 追従で left/top を更新)。
    <div
      data-oekaki-imagepaste-toast
      // a11y: 重要な拒否通知なので polite live region でスクリーンリーダーへ通知。
      // assertive ではなく polite (= 連続拒否のカウントダウンで読み上げを荒らさない)。
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: center.left,
        top: center.top,
        transform: 'translate(-50%, -50%)',
        zIndex: 70,
        // クリックを透過させ、下のキャンバス操作を邪魔しない。
        pointerEvents: 'none',
        maxWidth: 'min(90vw, 360px)',
      }}
      className="select-none whitespace-pre-line rounded-lg bg-black/75 px-4 py-2.5 text-center font-bold text-sm text-white shadow-lg"
    >
      {toast.text}
    </div>
  );
};
